import React, { useState, useRef } from "react";
import {
  Eye,
  Trash2,
  CloudDownload,
  CloudUpload,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useSelector } from "react-redux";
import { ErrorPopup } from "../../../../components/ErrorHandling";
import { Download } from "lucide-react";

// Upload status type
export type UploadStatus = "pending" | "submitted" | "error";

interface UploadBtnProps {
  modalTitle?: string;
  uploadStatus?: UploadStatus;
  uploadedFileName?: string;
  onDataParsed?: (data: any[], fileName: string) => void;
  onFileDeleted?: () => void;
  onUploadError?: () => void; // Callback when upload fails
}

export default function UploadBtn({
  modalTitle = "Upload template",
  uploadStatus = "pending",
  uploadedFileName,
  onDataParsed,
  onFileDeleted,
  onUploadError,
}: UploadBtnProps) {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const SelectedStartDate = useSelector(
    (state: any) => state.ProcurementOptimizerDemoDate.startDate
  );
  const SelectedEndDate = useSelector(
    (state: any) => state.ProcurementOptimizerDemoDate.endDate
  );

  const handleDownloadTemplate = () => {
    let exportData: any[] = [];
    const start = new Date(SelectedStartDate);
    const end = new Date(SelectedEndDate);

    for (
      let currentDate = new Date(start);
      currentDate <= end;
      currentDate.setDate(currentDate.getDate() + 1)
    ) {
      const formattedDate = currentDate.toISOString().split("T")[0];

      for (let i = 0; i < 96; i++) {
        exportData.push({
          date: formattedDate,
          time_slot: i + 1,
          deficit: "",
        });
      }
    }

    const headerMap: { [key: string]: string } = {
      date: "Date",
      time_slot: "Time Block",
      deficit: "Surplus (+)/Deficit (-)",
    };

    const keys = Object.keys(headerMap);

    const mappedData = exportData.map((row: Record<string, any>) =>
      keys.reduce((acc, key) => {
        acc[key] = row[key];
        return acc;
      }, {} as any)
    );

    const worksheet = XLSX.utils.json_to_sheet(mappedData, { header: keys });

    XLSX.utils.sheet_add_aoa(worksheet, [keys.map((k) => headerMap[k])], {
      origin: "A1",
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `${modalTitle}.xlsx`);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      parseFile(file);
    }
  };

  const parseFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });

        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        const headerReverseMap: { [key: string]: string } = {
          Date: "date",
          "Time Block": "time_slot",
          "Surplus (+)/Deficit (-)": "deficit",
        };

        const firstRow = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
        })[0] as string[];

        const missingHeaders = Object.keys(headerReverseMap).filter(
          (header) => !firstRow.includes(header)
        );

        if (missingHeaders.length > 0) {
          setError(
            `Invalid file format. Missing columns: ${missingHeaders.join(", ")}`
          );
          setUploadedFile(null);
          return;
        }

        const formattedData = (jsonData as any[]).map((row) => {
          const newRow: any = {};
          Object.entries(headerReverseMap).forEach(
            ([excelHeader, internalKey]) => {
              newRow[internalKey] = row[excelHeader] ?? "";
            }
          );
          return newRow;
        });

        if (onDataParsed) {
          onDataParsed(formattedData, file.name);
        }
      } catch (error) {
        console.error("Error processing file:", error);
        setError("There was an error processing the file. Please try again.");
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleViewFile = () => {
    if (uploadedFile) {
      const url = URL.createObjectURL(uploadedFile);
      window.open(url, "_blank");
      URL.revokeObjectURL(url);
    }
  };

  const handleDeleteFile = () => {
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (onFileDeleted) {
      onFileDeleted();
    }
  };

  const getStatusDisplay = () => {
    switch (uploadStatus) {
      case "submitted":
        return {
          icon: <CheckCircle2 size={20} className="text-green-500" />,
          text: "Submitted",
          textColor: "#10B981",
          bgColor: "#ECFDF5",
        };
      case "error":
        return {
          icon: <XCircle size={20} className="text-red-500" />,
          text: "Error",
          textColor: "#EF4444",
          bgColor: "#FEF2F2",
        };
      case "pending":
      default:
        return {
          icon: "",
          text: "",
          textColor: "#9CA3AF",
          bgColor: "#F9FAFB",
        };
    }
  };

  const statusDisplay = getStatusDisplay();

  return (
    <div className="flex items-center justify-between bg-[#FFFFFF] rounded-2xl border border-[#CAD5E2] px-2 py-2 w-full">
      <div className="flex items-center gap-2 justify-between w-full">
        <div className="flex flex-col gap-2">
          <span className="text-[#6F6D7C] text-sm font-medium">
            System Balance Profile (Surplus/Deficit)
          </span>
        </div>

        <div className="flex items-center gap-4 justify-end">
          <div className="flex items-center gap-3">
            <div
              className="flex items-center gap-2 px-3 py-1.5 rounded-xl"
              style={{
                backgroundColor: statusDisplay.bgColor,
              }}
            >
              {statusDisplay.icon}
              <span
                className="text-sm font-medium"
                style={{ color: statusDisplay.textColor }}
              >
                {statusDisplay.text}
              </span>
            </div>

            {uploadedFileName && uploadStatus === "submitted" && !uploadedFile && (
              <span className="text-sm text-[#6F6D7C] truncate max-w-[200px]">
                {uploadedFileName}
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            {uploadedFile ? (
              <div className="flex items-center gap-3 p-2 rounded-xl">
                <span className="text-sm text-[#525062] truncate max-w-[250px]">
                  {uploadedFile.name}
                </span>
                <button
                  onClick={handleViewFile}
                  className="text-orange-500 hover:text-orange-600 transition-colors"
                  title="View file"
                >
                  <Eye size={20} />
                </button>
                <button
                  onClick={handleDeleteFile}
                  className="text-[#6F6D7C] hover:text-red-500 transition-colors"
                  title="Delete file"
                >
                  <Trash2 size={20} />
                </button>
              </div>
            ) : (
              <>
                <input
                  type="file"
                  accept=".xlsx"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  className="hidden"
                />
                {!uploadedFile && uploadStatus !== "submitted" && (
                  <button
                    type="button"
                    onClick={handleDownloadTemplate}
                    className="w-[129px] h-[36px]  flex items-center justify-center gap-2 text-sm rounded-xl border border-[#F1935C] font-medium transition-colors hover:opacity-90"
                    style={{ backgroundColor: "#FFFAF6", color: "#F1935C" }}
                  >
                    <Download strokeWidth={3} size={18} /> Template
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleUploadClick}
                  className="w-[129px] h-[36px] flex items-center justify-center gap-2 text-sm rounded-xl border border-[#34656D] font-medium transition-colors hover:bg-[#EFFCFF]"
                  style={{ backgroundColor: "#EFFCFF", color: "#34656D" }}
                >
                  <CloudUpload strokeWidth={3} size={20} /> Upload
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      <ErrorPopup error={error} onClose={() => setError(null)} />
    </div>
  );
}
